import type { PaymentMethodRow } from './paymentMethodsView'
import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import type { SiteName } from '@/scopes/common/domain/types'

type PaymentMethodsPolicyConfig = Partial<Record<SiteName, PaymentMethod[]>>

const PAYMENT_METHODS_POLICY: PaymentMethodsPolicyConfig = {
  sunwebnl: ['ideal', 'banktransfer', 'klarna', 'klarna_account', 'creditcard', 'voucher'],
  sunwebbenl: ['bcmc', 'bcmc_mobile', 'banktransfer', 'creditcard', 'klarna', 'voucher'],
  sunwebbefr: ['bcmc', 'bcmc_mobile', 'banktransfer', 'creditcard', 'klarna', 'voucher'],
  sunwebfr: ['creditcard', 'alma', 'voucher'],
  sunwebdk: ['mobilepay', 'creditcard', 'klarna', 'klarna_account', 'trustly', 'banktransfer', 'voucher'],
  sunwebse: ['swish', 'trustly', 'creditcard', 'klarna_account', 'klarna', 'voucher'],
  sunwebuk: ['trustly', 'banktransfer', 'creditcard', 'klarna', 'klarna_account', 'voucher'],
  sunwebde: ['banktransfer', 'creditcard', 'klarna', 'voucher'],
  elizanl: ['ideal', 'banktransfer', 'klarna', 'klarna_account', 'creditcard', 'voucher'],
  elizabe: ['bcmc', 'bcmc_mobile', 'banktransfer', 'creditcard', 'klarna', 'voucher'],
}

function toCaseInsensitiveSet(values: string[]) {
  return new Set(values.map(value => value.toLowerCase()))
}

function matchesConfiguredMethod(method: PaymentMethodRow, configuredMethods: Set<string>) {
  if (configuredMethods.has(method.key.toLowerCase())) {
    return true
  }

  return method.paymentMethodTypes.some(paymentMethodType =>
    configuredMethods.has(paymentMethodType.toLowerCase()),
  )
}

function getSortedPaymentMethods(paymentMethods: PaymentMethodRow[], order: PaymentMethod[]) {
  return [...paymentMethods].sort((left, right) => {
    const leftIndex = order.findIndex(key =>
      left.key.toLowerCase() === key.toLowerCase()
      || left.paymentMethodTypes.some(paymentMethodType => paymentMethodType.toLowerCase() === key.toLowerCase()),
    )
    const rightIndex = order.findIndex(key =>
      right.key.toLowerCase() === key.toLowerCase()
      || right.paymentMethodTypes.some(paymentMethodType => paymentMethodType.toLowerCase() === key.toLowerCase()),
    )

    return leftIndex - rightIndex
  })
}

function getEnabledPaymentMethods(paymentMethods: PaymentMethodRow[], enabledMethods: PaymentMethod[]) {
  const enabledMethodSet = toCaseInsensitiveSet(enabledMethods)
  return paymentMethods.filter(method => matchesConfiguredMethod(method, enabledMethodSet))
}

export function getPaymentMethodsPolicyTransformer(
  siteName: SiteName,
  policyConfig: PaymentMethodsPolicyConfig = PAYMENT_METHODS_POLICY,
): (paymentMethods: PaymentMethodRow[]) => PaymentMethodRow[] {
  if (!siteName) {
    return paymentMethods => paymentMethods
  }

  const policy = policyConfig[siteName]

  if (!policy) {
    return paymentMethods => paymentMethods
  }

  return (paymentMethods) => {
    const enabledPaymentMethods = getEnabledPaymentMethods(paymentMethods, policy)
    return getSortedPaymentMethods(enabledPaymentMethods, policy)
  }
}
